// Firebase Base CRUD Operations
// ==============================
// Generic document operations that can be reused across services

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  Timestamp,
  DocumentData,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './config';

/**
 * Create a new document in a collection
 * @param collectionName The Firestore collection name
 * @param data Document data to create
 * @returns Result with success status and created document id
 */
export const createDocument = async <T extends DocumentData>(
  collectionName: string,
  data: T
) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: Timestamp.now(),
    });
    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Get a single document by ID
 * @param collectionName The Firestore collection name
 * @param documentId The document ID
 * @returns Result with success status and document data
 */
export const getDocument = async <T>(
  collectionName: string,
  documentId: string
): Promise<{ success: boolean; data?: T; error?: string }> => {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } as T };
    } else {
      return { success: false, error: 'Document not found' };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing document
 * @param collectionName The Firestore collection name
 * @param documentId The document ID
 * @param data Partial data to update
 * @returns Result with success status
 */
export const updateDocument = async (
  collectionName: string,
  documentId: string,
  data: Partial<DocumentData>
) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Upsert: Creates the document if it doesn't exist, or updates it if it does
 * @param collectionName The Firestore collection name
 * @param documentId The document ID
 * @param data Data to upsert
 * @returns Result with success status
 */
export const upsertDocument = async (
  collectionName: string,
  documentId: string,
  data: Partial<DocumentData>
) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await setDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
    }, { merge: true });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Delete a document
 * @param collectionName The Firestore collection name
 * @param documentId The document ID
 * @returns Result with success status
 */
export const deleteDocument = async (collectionName: string, documentId: string) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Query documents with constraints
 * @param collectionName The Firestore collection name
 * @param constraints Query constraints (where, orderBy, limit, etc.)
 * @returns Result with success status and array of documents
 */
export const queryDocuments = async <T>(
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<{ success: boolean; data?: T[]; error?: string }> => {
  try {
    const q = query(collection(db, collectionName), ...constraints);
    const querySnapshot = await getDocs(q);
    const documents = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as T)
    );
    return { success: true, data: documents };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Re-export commonly used Firestore utilities for convenience
export { collection, doc, query, Timestamp };
export { where, orderBy, limit } from 'firebase/firestore';
